import 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { getApp, initializeApp } from 'firebase/app';
import {
  Auth,
  getAuth,
  setPersistence,
  browserLocalPersistence,
  inMemoryPersistence,
  indexedDBLocalPersistence,
  initializeAuth,
  onAuthStateChanged,
  signInWithCustomToken,
  signOut,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import axios from 'axios';
import { sendClientLog } from './client-log';
import httpService from './httpService';
import { Subject } from 'rxjs';

const ENV = (import.meta as any).env;
const DEBUG_LOGS = ENV?.VITE_APP_DEBUG_LOGS === 'true';
const ENABLE_IOS_PERSISTENCE_FIX = ENV?.VITE_ENABLE_IOS_PERSISTENCE_FIX !== 'false';

const firebaseCustomToken = ENV.VITE_APP_FIREBASE_CUSTOM_TOKEN;
const org = ENV.VITE_APP_ORG;

const firebaseConfig = {
  apiKey: ENV.VITE_APP_FIREBASE_KEY,
  authDomain: ENV.VITE_APP_AUTH_DOMAIN,
  projectId: ENV.VITE_APP_PROJECT_ID,
  storageBucket: ENV.VITE_APP_STORAGE_BUCKET,
  messagingSenderId: ENV.VITE_APP_SENDER_ID,
  appId: ENV.VITE_APP_ID,
  measurementId: ENV.VITE_APP_MEASUREMENT_ID,
  tenantId: ENV?.VITE_APP_TENANT || 'plenitude-wjxil',
};

const FirebaseApp = initializeApp(firebaseConfig);
if (DEBUG_LOGS) {
  try {
    console.log('[firebase:init]', {
      tenantId: firebaseConfig.tenantId,
      isNative: Capacitor.isNativePlatform(),
      ua: typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a',
    });
    sendClientLog({ tag: '[firebase:init]', tenantId: firebaseConfig.tenantId, isNative: Capacitor.isNativePlatform(), ua: typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a' });
  } catch {}
}

export let auth: Auth;
export const tokenChange = new Subject<string>();

if (Capacitor.isNativePlatform()) {
  auth = initializeAuth(getApp(), {
    persistence: indexedDBLocalPersistence,
  });
} else {
  auth = getAuth(FirebaseApp);
}

auth.tenantId = firebaseConfig.tenantId;

// Configure web persistence with fallbacks (helps on iOS Chrome)
function configurePersistence() {
  // Native already uses indexedDBLocalPersistence via initializeAuth
  if (Capacitor.isNativePlatform()) return;
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isIOSWebKit = /iPhone|iPad|iPod/.test(ua);
  const isCriOS = /CriOS\//.test(ua);
  const tryChain = async () => {
    try {
      if (ENABLE_IOS_PERSISTENCE_FIX && (isIOSWebKit || isCriOS)) {
        await setPersistence(auth, browserLocalPersistence);
        if (DEBUG_LOGS) try { console.log('[firebase:persistence] browserLocalPersistence (iOS fix)'); sendClientLog({ tag: '[firebase:persistence]', value: 'browserLocalPersistence (iOS fix)' }); } catch {}
        return;
      }
      await setPersistence(auth, indexedDBLocalPersistence);
      if (DEBUG_LOGS) try { console.log('[firebase:persistence] indexedDBLocalPersistence'); sendClientLog({ tag: '[firebase:persistence]', value: 'indexedDBLocalPersistence' }); } catch {}
    } catch {
      try {
        await setPersistence(auth, browserLocalPersistence);
        if (DEBUG_LOGS) try { console.log('[firebase:persistence] browserLocalPersistence (fallback)'); sendClientLog({ tag: '[firebase:persistence]', value: 'browserLocalPersistence (fallback)' }); } catch {}
      } catch {
        try {
          await setPersistence(auth, inMemoryPersistence);
          if (DEBUG_LOGS) try { console.log('[firebase:persistence] inMemoryPersistence'); sendClientLog({ tag: '[firebase:persistence]', value: 'inMemoryPersistence' }); } catch {}
        } catch {
          if (DEBUG_LOGS) try { console.warn('[firebase:persistence] failed to set any persistence'); sendClientLog({ tag: '[firebase:persistence]', value: 'failed' }); } catch {}
        }
      }
    }
  };
  void tryChain();
}

configurePersistence();

// One-time readiness gate to wait initial auth hydration
let authInitialized = false;
const authReady: Promise<void> = new Promise((resolve) => {
  onAuthStateChanged(auth, () => {
    if (!authInitialized) {
      authInitialized = true;
      if (DEBUG_LOGS) try { console.log('[firebase:authReady] initial auth event'); sendClientLog({ tag: '[firebase:authReady]' }); } catch {}
      resolve();
    }
  });
});

export class FirebaseService {
  async getCustomToken(email: string, password: string) {
    const endpoint = firebaseCustomToken;
    const data = {
      user: email,
      password: password,
      org: org,
    };

    try {
      const response = await httpService.post({
        url: endpoint,
        data: data,
      });
      return (response as any).data.token;
    } catch (error: any) {
      throw new Error(error.code);
    }
  }

  async signInWithToken(token: string) {
   
    return signInWithCustomToken(auth, token);
  }

  async signInWithUserNameAndPassord(email: string, password: string) {
    // alert('signInWithUserNameAndPassord');
    return signInWithEmailAndPassword(auth, email, password);
  }

  onAuthStateChange(callback: (user: any) => void) {
    return onAuthStateChanged(auth, (user) => {
      if (DEBUG_LOGS) {
        try {
          const payload = {
            hasUser: Boolean(user),
            uid: (user && (user as any).uid) || null,
            email: (user && (user as any).email) || null,
            visibility: typeof document !== 'undefined' ? document.visibilityState : 'n/a',
          };
          console.log('[firebase:onAuthStateChanged]', payload);
          sendClientLog({ tag: '[firebase:onAuthStateChanged]', ...payload });
        } catch {}
      }
      callback(user);
    });
  }

  async getIdToken(forceRefresh = false): Promise<string | undefined> {
    // Wait for initial auth hydration to avoid false "no token" on cold start
    try {
      const withTimeout = <T>(p: Promise<T>, ms: number) => new Promise<T>((resolve) => {
        let settled = false;
        const timer = setTimeout(() => { if (!settled) resolve(undefined as unknown as T); }, ms);
        p.then((v) => { if (!settled) { settled = true; clearTimeout(timer); resolve(v); } }).catch(() => { if (!settled) { settled = true; clearTimeout(timer); resolve(undefined as unknown as T); } });
      });
      await withTimeout(authReady, 1500);
    } catch {}

    const user = auth.currentUser;
    if (user) {
      try {
        const token = await user.getIdToken(forceRefresh);
        return token;
      } catch {
        return undefined;
      }
    }
    return undefined;
  }

  // Explicitly refresh the token
  async refreshToken(): Promise<string | undefined> {
    return this.getIdToken(true);
  }

  getCurrentUserEmail(): string | null {
    const user = auth.currentUser;
    return user ? user.email : null;
  }

  getCurrentUserDisplayname(): string | null {
    const user = auth.currentUser;
    return user ? user.displayName : null;
  }

  async signOut(): Promise<void> {
    await signOut(auth);
  }

  setupTokenChangeListener() {
    auth.onIdTokenChanged(async (user) => {
      if (user) {
        try {
          const token = await user.getIdToken();
          tokenChange.next(token);
          if (DEBUG_LOGS) {
            try {
              const payload = { hasUser: true, uid: (user as any)?.uid || null };
              console.log('[firebase:onIdTokenChanged]', payload);
              sendClientLog({ tag: '[firebase:onIdTokenChanged]', ...payload });
            } catch {}
          }
        } catch (error) {
          // console.error('Error fetching token:', error);
        }
      } else {
        // Handle the case when the user is signed out
        tokenChange.next(undefined); //TODO - Z Correctly handle the undefined state if not already done
        if (DEBUG_LOGS) {
          try {
            console.log('[firebase:onIdTokenChanged]', { hasUser: false });
            sendClientLog({ tag: '[firebase:onIdTokenChanged]', hasUser: false });
          } catch {}
        }
      }
    });
  }
  
}
